import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Button, Card, Col, Form, Modal, Spinner,
} from 'react-bootstrap';
// @ts-ignore: no def file
import BootstrapTable from 'react-bootstrap-table-next';
// @ts-ignore: no def file
import { useHistory } from 'react-router';
import { PageLayout } from '../../../dorian-shared/types/pageLayout/PageLayout';
import { ToolRole } from '../../../dorian-shared/types/user/User';
import { api } from '../../api';
import { getGrantableTools, TOOL_ROLES } from '../../utils/toolAccessUtils';
import { ToolComponentProps } from '../ToolPage/ToolPage';
import { PageWrapper } from '../../ui/PageWrapper';
import { showToast } from '../../ui/utils';

const PAGE_SIZE = 10;

interface ToolUserRow {
  id: number;
  email: string;
  username: string | null;
  fullname: string;
  firstName: string | null;
  lastName: string | null;
  role: ToolRole;
  implicit?: boolean;
}

interface ToolUsersResponse {
  tool: { key: string; title: string; roles: ToolRole[] };
  users: ToolUserRow[];
  admins: ToolUserRow[];
  pagination: { page: number; limit: number; total: number };
}

interface AddToolUsersResult {
  added: ToolUserRow[];
  updated: ToolUserRow[];
  errors: { identifier: string; reason: string }[];
}

type SortField = 'id' | 'email';
type SortOrder = 'asc' | 'desc';

interface TableSortState {
  dataField: SortField;
  order: SortOrder;
}

const DEFAULT_TABLE_SORT: TableSortState = {
  dataField: 'id',
  order: 'asc',
};

function isSameSort(a: TableSortState, b: TableSortState) {
  return a.dataField === b.dataField && a.order === b.order;
}

function roleLabel(role: ToolRole) {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

const ADMIN_COLUMNS = [
  { dataField: 'id', text: 'User ID', sort: true },
  { dataField: 'email', text: 'Email', sort: true },
  {
    dataField: 'role',
    text: 'Role',
    sort: false,
    formatter: (value: ToolRole) => `${roleLabel(value)} (admin)`,
  },
];

function buildUserColumns(
  acceptedRoles: ToolRole[],
  onRoleChange: (userId: number, role: ToolRole) => void,
  onRemove: (userId: number) => void,
) {
  return [
    { dataField: 'id', text: 'User ID', sort: true },
    { dataField: 'email', text: 'Email', sort: true },
    {
      dataField: 'role',
      text: 'Role',
      sort: false,
      formatter: (value: ToolRole, row: ToolUserRow) => (
        <Form.Control
          as="select"
          size="sm"
          value={value}
          onChange={(event) => onRoleChange(row.id, event.target.value as ToolRole)}
        >
          {acceptedRoles.map((role) => (
            <option key={role} value={role}>
              {roleLabel(role)}
            </option>
          ))}
        </Form.Control>
      ),
    },
    {
      dataField: 'actions',
      isDummyField: true,
      text: '',
      sort: false,
      formatter: (_value: unknown, row: ToolUserRow) => (
        <Button
          size="sm"
          variant="outline-danger"
          onClick={() => onRemove(row.id)}
        >
          Remove
        </Button>
      ),
    },
  ];
}

export function ToolPermissions(props: ToolComponentProps) {
  const { auth } = props;
  const user = auth.getUser();
  const history = useHistory();
  const grantableTools = useMemo(() => getGrantableTools(user), [user]);

  const [selectedTool, setSelectedTool] = useState<string>(grantableTools[0]?.key || '');
  const [acceptedRoles, setAcceptedRoles] = useState<ToolRole[]>(TOOL_ROLES);
  const [admins, setAdmins] = useState<ToolUserRow[]>([]);
  const [grantedUsers, setGrantedUsers] = useState<ToolUserRow[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [identifiers, setIdentifiers] = useState('');
  const [addRole, setAddRole] = useState<ToolRole>('viewer');
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [grantedSort, setGrantedSort] = useState<TableSortState>(DEFAULT_TABLE_SORT);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [hasLoadedUsers, setHasLoadedUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadUsersRequestId = useRef(0);

  const pageLayout: PageLayout = {
    header: {
      title: 'Tool Permissions',
      settings: 'admin',
    },
    sidebar: {
      nav: [
        {
          title: 'Back',
          action: () => history.goBack(),
          variant: 'secondary',
          disabled: false,
        },
      ],
    },
  };

  const loadToolUsers = useCallback(() => {
    if (!selectedTool) {
      setLoadingUsers(false);
      return;
    }

    const requestId = loadUsersRequestId.current + 1;
    loadUsersRequestId.current = requestId;
    setLoadingUsers(true);
    setError(null);

    api.get<ToolUsersResponse>(`/v1/tools/${selectedTool}/users`, {
      params: {
        page: pageNumber,
        limit: PAGE_SIZE,
        search: search || undefined,
        sortField: grantedSort.dataField,
        sortOrder: grantedSort.order,
      },
    })
      .then((res) => {
        if (loadUsersRequestId.current !== requestId) {
          return;
        }
        setAdmins(res.data.admins || []);
        setGrantedUsers(res.data.users || []);
        setTotalUsers(res.data.pagination?.total || 0);
        setAcceptedRoles(res.data.tool?.roles || TOOL_ROLES);
        setHasLoadedUsers(true);
      })
      .catch((err) => {
        if (loadUsersRequestId.current !== requestId) {
          return;
        }
        setError(err?.response?.data?.error || 'Failed to load tool users');
      })
      .finally(() => {
        if (loadUsersRequestId.current !== requestId) {
          return;
        }
        setLoadingUsers(false);
      });
  }, [grantedSort.dataField, grantedSort.order, pageNumber, search, selectedTool]);

  useEffect(() => {
    loadToolUsers();
  }, [loadToolUsers]);

  useEffect(() => {
    if (!selectedTool && grantableTools.length > 0) {
      setSelectedTool(grantableTools[0].key);
    }
  }, [grantableTools, selectedTool]);

  useEffect(() => {
    setAddRole((current) => (acceptedRoles.includes(current) ? current : acceptedRoles[0]));
  }, [acceptedRoles]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPageNumber(1);
    setSearch(searchInput.trim());
  };

  const handleAddUsers = () => {
    if (!identifiers.trim()) {
      showToast({ textMessage: 'Enter one or more emails or user IDs', variant: 'warning' });
      return;
    }

    setSaving(true);
    api.post<AddToolUsersResult>(`/v1/tools/${selectedTool}/users`, { identifiers, role: addRole })
      .then((res) => {
        const { added = [], updated = [], errors = [] } = res.data;
        const successCount = added.length + updated.length;
        if (successCount > 0) {
          showToast({ textMessage: `Updated access for ${successCount} user(s)`, variant: 'success' });
        }
        if (errors.length > 0) {
          showToast({
            textMessage: `${errors.length} identifier(s) could not be added: ${errors.map((e) => e.identifier).join(', ')}`,
            variant: 'warning',
          });
        }
        setIdentifiers('');
        setShowAddUsersModal(false);
        loadToolUsers();
      })
      .catch((err) => {
        showToast({ textMessage: err?.response?.data?.error || 'Failed to add users', variant: 'danger' });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleCloseAddUsersModal = () => {
    if (saving) {
      return;
    }
    setShowAddUsersModal(false);
    setIdentifiers('');
  };

  const handleGrantedTableChange = useCallback((
    type: string,
    { sortField, sortOrder }: { sortField?: string; sortOrder?: string },
  ) => {
    if (type !== 'sort' || !sortField) {
      return;
    }

    const nextSort: TableSortState = {
      dataField: sortField === 'email' ? 'email' : 'id',
      order: sortOrder === 'desc' ? 'desc' : 'asc',
    };

    setGrantedSort((current) => {
      if (isSameSort(current, nextSort)) {
        return current;
      }
      setPageNumber(1);
      return nextSort;
    });
  }, []);

  const handleRoleChange = useCallback((userId: number, role: ToolRole) => {
    api.patch(`/v1/tools/${selectedTool}/users/${userId}`, { role })
      .then(() => {
        showToast({ textMessage: 'Role updated', variant: 'success' });
        loadToolUsers();
      })
      .catch((err) => {
        showToast({ textMessage: err?.response?.data?.error || 'Failed to update role', variant: 'danger' });
      });
  }, [loadToolUsers, selectedTool]);

  const handleRemoveUser = useCallback((userId: number) => {
    api.delete(`/v1/tools/${selectedTool}/users/${userId}`)
      .then(() => {
        showToast({ textMessage: 'Access removed', variant: 'success' });
        loadToolUsers();
      })
      .catch((err) => {
        showToast({ textMessage: err?.response?.data?.error || 'Failed to remove access', variant: 'danger' });
      });
  }, [loadToolUsers, selectedTool]);

  const userColumns = useMemo(
    () => buildUserColumns(acceptedRoles, handleRoleChange, handleRemoveUser),
    [acceptedRoles, handleRemoveUser, handleRoleChange],
  );

  const totalPages = Math.max(Math.ceil(totalUsers / PAGE_SIZE), 1);
  const selectedToolTitle = grantableTools.find((tool) => tool.key === selectedTool)?.title
    || selectedTool;

  return (
    <PageWrapper user={user} page={pageLayout}>
      <Card>
        <Card.Body>
          {grantableTools.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No grantable tools yet. Add tools to the server catalog to manage access here.
            </Alert>
          ) : (
            <>
              <div className="mb-4 d-flex align-items-center justify-content-between">
                <Form.Group className="mb-0 d-flex align-items-center">
                  <Form.Label className="mb-0 mr-2">Tool</Form.Label>
                  <Form.Control
                    as="select"
                    className="w-auto"
                    style={{ minWidth: 220 }}
                    value={selectedTool}
                    onChange={(event) => {
                      setSelectedTool(event.target.value);
                      setPageNumber(1);
                      setSearch('');
                      setSearchInput('');
                      setGrantedSort(DEFAULT_TABLE_SORT);
                      setHasLoadedUsers(false);
                    }}
                  >
                    {grantableTools.map((tool) => (
                      <option key={tool.key} value={tool.key}>
                        {tool.title}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                <Button
                  variant="primary"
                  onClick={() => setShowAddUsersModal(true)}
                >
                  Add users
                </Button>
              </div>

              <Modal
                show={showAddUsersModal}
                onHide={handleCloseAddUsersModal}
                backdrop="static"
                centered
              >
                <Modal.Header closeButton={!saving}>
                  <Modal.Title>
                    Add users to
                    {' '}
                    {selectedToolTitle}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form.Group>
                    <Form.Label>Emails or user IDs (comma-separated)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={identifiers}
                      onChange={(event) => setIdentifiers(event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-0">
                    <Form.Label>Role</Form.Label>
                    <Form.Control
                      as="select"
                      value={addRole}
                      onChange={(event) => setAddRole(event.target.value as ToolRole)}
                    >
                      {acceptedRoles.map((role) => (
                        <option key={role} value={role}>
                          {roleLabel(role)}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    disabled={saving}
                    onClick={handleCloseAddUsersModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    disabled={saving}
                    onClick={handleAddUsers}
                  >
                    {saving ? <Spinner animation="border" size="sm" /> : 'Add users'}
                  </Button>
                </Modal.Footer>
              </Modal>

              <Form onSubmit={handleSearch} className="mb-3">
                <Form.Row>
                  <Col sm={8}>
                    <Form.Control
                      placeholder="Search by email or user ID"
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                    />
                  </Col>
                  <Col sm={4}>
                    <Button type="submit" variant="secondary">Search</Button>
                    {search && (
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => {
                          setSearch('');
                          setSearchInput('');
                          setPageNumber(1);
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Col>
                </Form.Row>
              </Form>

              {error && <Alert variant="danger">{error}</Alert>}

              <h5 className="mt-4">
                {selectedToolTitle}
                {' '}
                - granted users
              </h5>
              {!hasLoadedUsers && loadingUsers ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <BootstrapTable
                    keyField="id"
                    data={grantedUsers}
                    columns={userColumns}
                    bootstrap4
                    striped
                    hover
                    remote={{ sort: true }}
                    onTableChange={handleGrantedTableChange}
                    sort={grantedSort}
                    noDataIndication="No users have been granted access to this tool"
                  />
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span>
                      Page
                      {' '}
                      {pageNumber}
                      {' '}
                      of
                      {' '}
                      {totalPages}
                      {' '}
                      (
                      {totalUsers}
                      {' '}
                      users)
                      {loadingUsers && (
                        <>
                          {' '}
                          <Spinner animation="border" size="sm" />
                        </>
                      )}
                    </span>
                    <div>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="mr-2"
                        disabled={pageNumber <= 1}
                        onClick={() => setPageNumber((current) => Math.max(current - 1, 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={pageNumber >= totalPages}
                        onClick={() => setPageNumber((current) => Math.min(current + 1, totalPages))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}

              <div className="pb-5">
                <h5 className="mt-4">Admins</h5>
                <p className="text-muted">
                  Admins always have full access to every tool and cannot be removed.
                </p>
                {!hasLoadedUsers && loadingUsers ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <BootstrapTable
                    keyField="id"
                    data={admins}
                    columns={ADMIN_COLUMNS}
                    rowClasses={() => 'text-muted'}
                    bootstrap4
                    striped
                    hover
                    noDataIndication="No admin users found"
                  />
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </PageWrapper>
  );
}
